<template>
    <div class="main-content">
        <div class="container">
            <div class="header-section">
                <h1>Gene Catalog</h1>
                <p class="lead">Explore the gene coexpression and module associations.</p>
            </div>

            <div class="panel">
                <div class="search-bar">
                    <el-form :inline="true" ref="searchForm" :model="searchForm" label-width="80px" @submit.native.prevent>
                        <el-form-item>
                            <el-select v-model="searchForm.species" placeholder="Select Species" class="custom-input" style="width: 150px">
                                <el-option label="Human" value="Human"></el-option>
                                <el-option label="Mouse" value="Mouse"></el-option>
                            </el-select>
                        </el-form-item>
                        <el-form-item>
                            <el-input class="custom-input" style="width: 300px" v-model="searchForm.gene" clearable
                                placeholder="Search by gene symbol or Ensembl ID" prefix-icon="el-icon-search" />
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" @click="onFormSearch">Search</el-button>
                        </el-form-item>
                    </el-form>
                </div>

                <el-table :data="tableData" empty-text="No data available" class="custom-table"
                    @sort-change="handleTableSortChange">
                    <el-table-column prop="geneSymbol" label="Gene.Symbol" sortable="custom">
                        <template slot-scope="scope">
                            <a :href="`/#/gene/detail/${scope.row.id}?species=${searchForm.species}`" class="table-link">
                                {{ scope.row.geneSymbol }}
                            </a>
                        </template>
                    </el-table-column>
                    <el-table-column prop="ensemblId" label="Ensembl.ID" sortable="custom">
                        <template slot-scope="scope">
                            <a :href="`/#/gene/detail/${scope.row.id}?species=${searchForm.species}`" class="table-link">
                                {{ scope.row.ensemblId }}
                            </a>
                        </template>
                    </el-table-column>
                    <el-table-column prop="geneLocation" label="Gene.Location" sortable="custom" />
                    <el-table-column prop="geneType" label="Gene.Type" sortable="custom" />
                    <el-table-column prop="fullName" label="Full.Name" sortable="custom" />
                    <el-table-column prop="synonyms" label="Synonyms" sortable="custom" />
                </el-table>

                <div class="pagination-wrapper">
                    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
                        :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pagination.pageSize"
                        layout="total, sizes, prev, pager, next" :total="pagination.total" />
                </div>
            </div>

            <div style="margin-bottom: 40px;"></div>
        </div>
    </div>
</template>

<script>
export default {
    name: "GeneList",
    data() {
        return {
            searchForm: {
                gene: this.$route.query.keyword || "",
                species: "Human"
            },
            tableData: [],
            pagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },
            sortProp: "geneSymbol",
            sortOrder: "asc"
        };
    },
    created() {
        this.getData();
    },
    methods: {
        async getData() {
            try {
                const params = new URLSearchParams();
                params.append('pageNum', this.pagination.currentPage);
                params.append('pageSize', this.pagination.pageSize);
                if (this.searchForm.gene) {
                    params.append('gene', this.searchForm.gene);
                }
                if (this.searchForm.species) {
                    params.append('species', this.searchForm.species);
                }
                if (this.sortProp) {
                    params.append('orderBy', this.sortProp);
                }
                if (this.sortOrder) {
                    params.append('orderDirection', this.sortOrder);
                }

                const res = await this.$ajax.get(`/gene/page?${params.toString()}`);
                this.tableData = res.data.records;
                this.pagination.total = res.data.total;
            } catch (error) {
                console.error("Failed to fetch gene data:", error);
            }
        },
        onFormSearch() {
            this.pagination.currentPage = 1;
            this.getData();
        },
        handleTableSortChange({ prop, order }) {
            this.sortProp = prop;
            this.sortOrder = order === "ascending" ? "asc" : "desc";
            this.getData();
        },
        handleRowClick(row) {
            // 跳转到详情页
            this.$router.push(`/gene/detail/${row.id}?species=${this.searchForm.species}`);
        },
        handleSizeChange(size) {
            this.pagination.pageSize = size;
            this.getData();
        },
        handleCurrentChange(page) {
            this.pagination.currentPage = page;
            this.getData();
        }
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Playfair+Display:wght@600;700&display=swap');

:root {
    --bg: #dfe4ed;
    --panel: #f7f9fc;
    --navy: #0e1a28;
    --muted: #4a586b;
    --accent: #5d87b5;
    --border: #c7d1dd;
    --shadow: 0 20px 44px rgba(14, 26, 40, 0.18);
    --primary: #0e1a28;
}

.main-content {
    background-color: var(--bg);
    min-height: 100vh;
    padding: 30px 0;
    font-family: 'Inter', sans-serif;
    color: var(--navy);
}

.container {
    padding: 0 20px;
}

.header-section {
    margin-bottom: 30px;
    text-align: center;
}

h1 {
    font-family: 'Playfair Display', serif;
    font-size: 42px;
    font-weight: 700;
    color: var(--navy);
    margin: 0 0 10px 0;
}

.lead {
    color: var(--muted);
    font-size: 18px;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
}

.panel {
    background: linear-gradient(180deg, #f9fbfe 0%, #d8e1ee 100%);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 30px;
    box-shadow: var(--shadow);
}

.search-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
}

/* Custom Input */
::v-deep .custom-input .el-input__inner {
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 20px 16px;
    background: rgba(255, 255, 255, 0.8);
    color: var(--navy);
    font-family: 'Inter', sans-serif;
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
    transition: all 0.3s ease;
}

::v-deep .custom-input.el-input--prefix .el-input__inner {
    padding-left: 40px;
}

::v-deep .custom-input.el-input--suffix .el-input__inner {
    padding-right: 40px;
}

::v-deep .custom-input .el-input__inner:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(93, 135, 181, 0.2);
}

/* Custom Button */
::v-deep .el-button--primary {
    background: var(--accent);
    border: 1px solid var(--accent);
    border-radius: 12px;
    padding: 12px 24px;
    font-weight: 700;
    color: #0c1724;
    box-shadow: 0 8px 16px rgba(93, 135, 181, 0.2);
    font-family: 'Inter', sans-serif;
    transition: all 0.3s ease;
}

::v-deep .el-button--primary:hover {
    background: #4a7095;
    border-color: #4a7095;
    transform: translateY(-2px);
    box-shadow: 0 12px 20px rgba(93, 135, 181, 0.3);
}

/* Custom Table */
::v-deep .el-table.custom-table {
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
    background-color: rgba(255, 255, 255, 0.9);
}

::v-deep .el-table th {
    background-color: rgba(93, 135, 181, 0.15) !important;
    color: var(--navy);
    font-family: 'Playfair Display', serif;
    font-weight: 700;
    font-size: 15px;
    padding: 16px 0;
    border-bottom: 1px solid rgba(93, 135, 181, 0.2);
}

::v-deep .el-table td {
    padding: 14px 0;
    font-family: 'Inter', sans-serif;
    color: var(--muted);
    border-bottom: 1px solid rgba(199, 209, 221, 0.5);
}

::v-deep .el-table tr:hover > td {
    background-color: rgba(93, 135, 181, 0.08) !important;
}

.table-link {
    color: var(--accent);
    font-weight: 600;
    text-decoration: none;
    transition: color 0.2s;
}

.table-link:hover {
    color: var(--navy);
    text-decoration: underline;
}

.pagination-wrapper {
    margin-top: 30px;
    text-align: center;
}

::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
    background-color: var(--accent);
    color: #fff;
}
</style>
